<template>
  <div class="container my-4">
    <router-link :to="{ name: 'ComponentsView' }">
      <fa-icon :icon="['fas', 'chevron-left']" class="mr-2" />Komponenten
      verwalten
    </router-link>
    <h4 class="mt-3 mb-4">Neue Komponente erstellen</h4>
    <form v-on:submit.prevent="komponenteSpeichern">
      <div class="form-group">
        <label class="font-weight-bold">Bezeichnung</label>
        <input
          type="text"
          class="form-control w-50"
          v-model="name"
          placeholder="Grafikkarte"
          ref="komponentenNameInput"
          v-on:focus="markieren('komponentenNameInput')"
        />
      </div>
      <div class="form-group">
        <label class="font-weight-bold">Kategorie</label>
        <b-form-select
          class="d-block w-50"
          v-model="selectedCategory"
          :options="categoryOptions"
        ></b-form-select>
      </div>
      <div class="form-group">
        <label class="font-weight-bold">Hinweis</label>
        <textarea class="form-control w-50" v-model="notice"></textarea>
      </div>
      <div class="form-group" v-if="selectedCategory === 'Tastatur'">
        <label class="font-weight-bold"
          >Varianten <small>(optional)</small></label
        >
        <fa-icon icon="question-circle" fixed-width :id="`popover-1`"></fa-icon>
        <b-popover target="popover-1" triggers="hover focus">
          <p>
            Du <u>kannst</u> durch die Verwendung von Varianten die Anzahl der
            Optionen erhöhen.
          </p>
          <p>
            <b>Jede Variante enthält alle Optionen.</b> Die Preisabweichung wird
            in den Varianten festgelegt.
          </p>
          <p>
            Du kannst durch Ziehen und Ablegen die Reihefolge der Varianten
            ändern.
          </p>
        </b-popover>
        <div class="form-group" v-if="variants.length > 1">
          <button
            type="button"
            class="btn btn-link pl-0"
            v-on:click="toggleAllVariants(true)"
          >
            Alle aufklappen</button
          >/<button
            type="button"
            class="btn btn-link"
            v-on:click="toggleAllVariants(false)"
          >
            Alle einklappen
          </button>
        </div>
        <div class="form-group">
          <draggable
            v-model="variants"
            group="variants"
            @start="drag = true"
            @end="drag = false"
            handle=".handle"
          >
            <div
              v-for="(element, index) in variants"
              :key="` variant-${index}`"
              class="mb-2"
            >
              <div class="d-flex variant">
                <div
                  class="
                    d-flex
                    align-items-center
                    grip
                    bg-secondary
                    text-white
                    px-1
                    handle
                  "
                >
                  <fa-icon icon="grip-vertical" fixed-width />
                </div>
                <div
                  class="
                    d-flex
                    align-items-center
                    border-top border-bottom
                    px-3
                    active-control
                  "
                >
                  <b-form-checkbox v-model="variants[index].aktiv" switch
                    ><small>{{
                      status(variants[index].aktiv)
                    }}</small></b-form-checkbox
                  >
                </div>
                <button
                  type="button"
                  class="btn btn-light text-left border rounded-0 flex-grow-1"
                  v-b-toggle="`variant-${index}`"
                >
                  <fa-icon
                    icon="chevron-circle-down"
                    fixed-width
                    v-if="variants[index].offen === false"
                  />
                  <fa-icon icon="chevron-circle-up" fixed-width v-else />
                  <span class="ml-2"
                    >({{ ctoVariantCode(index) }})
                    {{ variants[index].name }}</span
                  >
                </button>
                <button
                  type="button"
                  class="delete btn btn-danger"
                  v-on:click="variantEntfernen(variants[index].id)"
                >
                  <fa-icon icon="trash" fixed-width />
                </button>
              </div>
              <b-collapse
                :id="`variant-${index}`"
                class="mt-1"
                v-model="variants[index].offen"
              >
                <div class="card">
                  <div class="card-body">
                    <div class="form-row">
                      <div class="col-5 rezisive-input-type5">
                        <label class="font-weight-bold">Bezeichnung</label>
                        <input
                          type="text"
                          class="form-control"
                          v-model="variants[index].name"
                          placeholder="Variante 1"
                          ref="nameVariantInput"
                          v-on:focus="
                            variantInputMarkieren('nameVariantInput', index)
                          "
                        />
                      </div>
                      <div class="col-3 rezisive-input-type3">
                        <label class="font-weight-bold">Kurzbezeichnung</label>
                        <input
                          type="text"
                          class="form-control"
                          v-model="variants[index].nameKurz"
                          placeholder="Opt. 1"
                          ref="variantNameKurzInput"
                          v-on:focus="
                            variantInputMarkieren('variantNameKurzInput', index)
                          "
                        />
                      </div>
                      <div class="col rezisive-input-type0">
                        <label class="font-weight-bold">UVP-Brutto Abw.</label>
                        <input
                          type="number"
                          class="form-control"
                          v-model.number="variants[index].uvpBruttoAbweichung"
                          min="0"
                          step=".01"
                          ref="variantUvpBruttoInput"
                          v-on:focus="
                            variantInputMarkieren(
                              'variantUvpBruttoInput',
                              index
                            )
                          "
                        />
                      </div>
                      <div class="col rezisive-input-type0">
                        <label class="font-weight-bold">UVP-Netto Abw.</label>
                        <input
                          type="number"
                          class="form-control"
                          readonly
                          v-model.number="variants[index].dacAbweichung"
                          min="0"
                          ref="variantDacInput"
                          v-on:focus="
                            variantInputMarkieren('variantDacInput', index)
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>
          </draggable>
        </div>
        <button type="button" class="btn btn-link" @click="varianteHinzufuegen">
          <fa-icon icon="plus" fixed-width class="mr-1" /> Variante hinzufügen
        </button>
        <hr />
      </div>
      <div class="form-group">
        <label class="font-weight-bold">Optionen</label>
        <p>
          Du kannst durch Ziehen und Ablegen die Reihefolge der Optionen ändern.
        </p>
        <div class="form-group">
          <button
            type="button"
            class="btn btn-link pl-0"
            v-on:click="toggleAll(true)"
          >
            Alle aufklappen</button
          >/<button
            type="button"
            class="btn btn-link"
            v-on:click="toggleAll(false)"
          >
            Alle einklappen
          </button>
        </div>
        <draggable
          v-model="optionen"
          group="optionen"
          @start="drag = true"
          @end="drag = false"
          handle=".handle"
        >
          <div
            v-for="(element, index) in optionen"
            :key="`option-${index}`"
            class="mb-2"
          >
            <div class="d-flex option">
              <div
                class="
                  d-flex
                  align-items-center
                  grip
                  bg-secondary
                  text-white
                  px-1
                  handle
                "
              >
                <fa-icon icon="grip-vertical" fixed-width />
              </div>
              <div
                class="
                  d-flex
                  align-items-center
                  border-top border-bottom
                  px-3
                  active-control
                "
              >
                <b-form-checkbox v-model="optionen[index].aktiv" switch
                  ><small>{{
                    status(optionen[index].aktiv)
                  }}</small></b-form-checkbox
                >
              </div>
              <button
                type="button"
                class="btn btn-light text-left border rounded-0 flex-grow-1"
                v-b-toggle="`option-${index}`"
              >
                <fa-icon
                  icon="chevron-circle-down"
                  fixed-width
                  v-if="optionen[index].offen === false"
                />
                <fa-icon icon="chevron-circle-up" fixed-width v-else />
                <span class="ml-2"
                  >({{ ctoOptionCode(index) }}) {{ optionen[index].name }}</span
                >
              </button>
              <button
                type="button"
                class="delete btn btn-danger"
                v-on:click="optionEntfernen(optionen[index].id)"
              >
                <fa-icon icon="trash" fixed-width />
              </button>
            </div>
            <b-collapse
              :id="`option-${index}`"
              class="mt-1"
              v-model="optionen[index].offen"
            >
              <div class="card">
                <div class="card-body">
                  <div class="form-row">
                    <div class="col-5 rezisive-input-type5">
                      <label class="font-weight-bold">Bezeichnung</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="optionen[index].name"
                        placeholder="Option 1"
                        ref="nameInput"
                        v-on:focus="optionInputMarkieren('nameInput', index)"
                      />
                    </div>
                    <div class="col-3 rezisive-input-type3">
                      <label class="font-weight-bold">Kurzbezeichnung</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="optionen[index].nameKurz"
                        placeholder="Opt. 1"
                        ref="nameKurzInput"
                        v-on:focus="
                          optionInputMarkieren('nameKurzInput', index)
                        "
                      />
                    </div>
                    <div
                      class="col rezisive-input-type0"
                      v-if="
                        variants.length < 1 || selectedCategory !== 'Tastatur'
                      "
                    >
                      <label class="font-weight-bold">UVP-Brutto Abw.</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="optionen[index].uvpBruttoAbweichung"
                        min="0"
                        step=".01"
                        ref="uvpBruttoInput"
                        v-on:focus="
                          optionInputMarkieren('uvpBruttoInput', index)
                        "
                      />
                    </div>
                    <div
                      class="col rezisive-input-type0"
                      v-if="
                        variants.length < 1 || selectedCategory !== 'Tastatur'
                      "
                    >
                      <label class="font-weight-bold">UVP-Netto Abw.</label>
                      <input
                        type="number"
                        class="form-control"
                        readonly
                        v-model.number="optionen[index].dacAbweichung"
                        min="0"
                        ref="dacInput"
                        v-on:focus="optionInputMarkieren('dacInput', index)"
                      />
                    </div>
                    <div
                      class="col"
                      v-if="
                        variants.length > 0 && selectedCategory === 'Tastatur'
                      "
                    >
                      <label class="font-weight-bold">Variantenfilter</label>
                      <b-badge
                        class="ml-1"
                        variant="success"
                        v-if="optionen[index].filter.length === variants.length"
                        >In allen Varianten Aktiv</b-badge
                      >
                      <b-badge
                        class="ml-1"
                        variant="warning"
                        v-if="optionen[index].filter.length !== variants.length"
                        >In {{ optionen[index].filter.length }} Varianten
                        Aktiv</b-badge
                      >
                      <div>
                        <b-btn @click="openFilterModal(index)"
                          >Filter anpassen</b-btn
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </draggable>
        <button
          type="button"
          class="btn btn-link mt-3"
          v-on:click="optionHinzufuegen"
        >
          <fa-icon icon="plus" fixed-width class="mr-1" />Option hinzufügen
        </button>
      </div>
      <SaveButtonComponent :saveButtonState="saveButtonState" />
    </form>
    <b-modal
      id="varianten-filter"
      ref="varianten-filter"
      title="Varianten filtern"
    >
      <template #default="{}">
        <b-alert variant="warning" show
          >Herausgefilterte Varianten werden beim Export für das deLUXE nicht
          berücksichtigt.</b-alert
        >
        Die Option <b>{{ filterName }}</b> ist in folgenden Varianten aktiv.
        <b-form-checkbox-group
          v-model="optionen[filterMeId].filter"
          :options="createVariantOptions()"
          class="ml-4"
          stacked
          switches
          variant="danger"
        ></b-form-checkbox-group>
      </template>
      <template #modal-footer="{ hide }">
        <b-button size="sm" variant="success" @click="hide('varianten-filter')">
          OK
        </b-button>
        <b-button
          size="sm"
          variant="outline-primary"
          @click="saveFilterForAll(filterMeId)"
        >
          Auf alle Optionen anwenden
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import SaveButtonComponent from "../components/SaveButtonComponent";

import { firestore } from "../plugins/firebase";
import { addDoc, collection } from "firebase/firestore";

export default {
  name: "CreateComponentView",
  components: {
    draggable,
    SaveButtonComponent,
  },
  data() {
    return {
      name: "",
      selectedCategory: "Arbeitsspeicher",
      notice: "",
      categoryOptions: [
        {
          text: "Arbeitsspeicher",
          value: "Arbeitsspeicher",
        },
        {
          text: "Armband",
          value: "Armband",
        },
        {
          text: "Display",
          value: "Display",
        },
        {
          text: "Ethernet",
          value: "Ethernet",
        },
        {
          text: "Gehäuse",
          value: "Gehäuse",
        },
        {
          text: "Gehäusefarbe",
          value: "Gehäusefarbe",
        },
        {
          text: "Gehäusegröße",
          value: "Gehäusegröße",
        },
        {
          text: "Grafik",
          value: "Grafik",
        },
        {
          text: "Konnektivität",
          value: "Konnektivität",
        },
        {
          text: "I/O",
          value: "I/O",
        },
        {
          text: "Massenspeicher",
          value: "Massenspeicher",
        },
        {
          text: "Netzteil",
          value: "Netzteil",
        },
        {
          text: "Prozessor",
          value: "Prozessor",
        },
        {
          text: "Software 1",
          value: "Software 1",
        },
        {
          text: "Software 2",
          value: "Software 2",
        },
        {
          text: "Tastatur",
          value: "Tastatur",
        },
        {
          text: "Zeigegerät",
          value: "Zeigegerät",
        },
      ],
      variants: [],
      optionen: [],
      isLoading: true,
      saveButtonState: "",
      filterId: null,
      filterName: "",
    };
  },
  watch: {
    optionen: {
      handler: function (optionen) {
        optionen.forEach((option) => {
          option.dacAbweichung = parseFloat(
            (option.uvpBruttoAbweichung / 1.19).toFixed(2)
          );
        });
      },
      deep: true,
    },
    variants: {
      handler: function (variants) {
        if (variants.length > 0) {
          this.optionen.forEach((el, index) => {
            this.optionen[index].uvpBruttoAbweichung = 0;
            this.optionen[index].dacAbweichung = 0;
            if (!this.optionen[index].filter) {
              this.optionen[index].filter = variants.map((x) => x.id);
            }
          });
        }
        variants.forEach((variants) => {
          variants.dacAbweichung = parseFloat(
            (variants.uvpBruttoAbweichung / 1.19).toFixed(2)
          );
        });
      },
      deep: true,
    },
  },
  methods: {
    komponenteSpeichern: function () {
      this.saveButtonState = "saving";
      let variantenTemp = [];
      if (this.variants.length > 0 && this.selectedCategory === "Tastatur") {
        variantenTemp = this.variants;
      }
      addDoc(collection(firestore, "komponenten"), {
        name: this.name,
        selectedCategory: this.selectedCategory,
        notice: this.notice,
        optionen: this.optionen,
        varianten: variantenTemp,
      })
        .then(() => {
          this.saveButtonState = "done";
          setTimeout(() => {
            this.saveButtonState = "";
          }, 3500);
        })
        .catch((error) => {
          console.error(error);
          this.saveButtonState = "error";
          setTimeout(() => {
            this.saveButtonState = "";
          }, 3500);
        }); // eslint-disable-line no-console
    },
    generiereId: function (idLength) {
      const alphaNum =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let id = "";
      for (let i = 0; i < idLength; i++) {
        id += alphaNum.charAt(Math.floor(Math.random() * alphaNum.length));
      }
      return id;
    },
    optionHinzufuegen: function () {
      const option = {
        name: "",
        filter: [],
        nameKurz: "",
        dacAbweichung: 0,
        uvpBruttoAbweichung: 0,
        offen: true,
        id: this.generiereId(10),
      };
      this.optionen.push(option);
      setTimeout(this.resizeInputFields, 10) ;
    },
    optionEntfernen: function (id) {
      for (let i = 0; i < this.optionen.length; i++) {
        if (this.optionen[i].id === id) {
          this.optionen.splice(i, 1);
          i--;
        }
      }
    },
    markieren: function (input) {
      this.$refs[input].select();
    },
    optionInputMarkieren: function (input, index) {
      this.$refs[input][index].select();
    },
    ctoOptionCode: function (index) {
      let ctoOptionCodes = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      ctoOptionCodes = ctoOptionCodes.split("");
      return ctoOptionCodes[index];
    },
    status: function (active) {
      return active ? "Aktiv" : "Inaktiv";
    },
    toggleAll: function (status) {
      for (let i = 0; i < this.optionen.length; i++) {
        this.optionen[i].offen = status;
      }
    },
    toggleAllVariants: function (status) {
      for (let i = 0; i < this.variants.length; i++) {
        this.variants[i].offen = status;
      }
    },
    varianteHinzufuegen: function () {
      const variant = {
        name: "",
        nameKurz: "",
        offen: true,
        aktiv: true,
        dacAbweichung: 0,
        uvpBruttoAbweichung: 0,
        id: this.generiereId(10),
      };
      this.optionen.map((option) => {
        if (!option.filter.includes(variant.id)) {
          option.filter.push(variant.id);
        }
      });
      this.variants.push(variant);
    },
    variantEntfernen: function (id) {
      this.optionen.forEach((option, index) => {
        const filterIndex = this.optionen[index].filter.findIndex(
          (i) => i === id
        );
        if (filterIndex >= 0) {
          this.optionen[index].filter.splice(filterIndex, 1);
        }
      });
      for (let i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) {
          this.variants.splice(i, 1);
          i--;
        }
      }
    },
    variantInputMarkieren: function (input, index) {
      this.$refs[input][index].select();
    },
    ctoVariantCode: function (index) {
      let ctoVarianteCodes = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      ctoVarianteCodes = ctoVarianteCodes.split("");
      return ctoVarianteCodes[index];
    },
    openFilterModal(optionsIndex) {
      this.filterMeId = optionsIndex;
      this.filterName = this.optionen[optionsIndex].name;
      this.$refs["varianten-filter"].show();
    },
    filterStatus(val) {
      if (val !== undefined) {
        return "Aktiv";
      } else {
        return "Inaktiv";
      }
    },
    changeOption(val) {
      alert(val);
    },
    createVariantOptions() {
      let arr = [];
      this.variants.forEach((o) => {
        arr.push({ value: o.id, text: o.name });
      });
      return arr;
    },
    saveFilterForAll(filterMeId) {
      this.optionen.map((o) => {
        o.filter = this.optionen[filterMeId].filter;
      });
      this.$refs["varianten-filter"].hide();
      return;
    },
    resizeInputFields() {
      const input5 = document.getElementsByClassName("rezisive-input-type5");
      const input3 = document.getElementsByClassName("rezisive-input-type3");
      const input0 = document.getElementsByClassName("rezisive-input-type0");
      let i = 0;

      if (window.innerWidth <= 991) {
        while (input5.length - i !== 0) {
          input5[i].classList.add("col-6");
          input3[i].classList.add("col-6");
          input3[i].classList.remove("col-3");
          input5[i].classList.remove("col-5");
          i++;
        }
        i = 0;
        while (input0.length - i !== 0) {
          input0[i].classList.add("col-6");
          input0[i].classList.remove("col");
          i++;
        }
      } else {
        while (input5.length - i !== 0) {
          input5[i].classList.remove("col-6");
          input3[i].classList.remove("col-6");
          input3[i].classList.add("col-3");
          input5[i].classList.add("col-5");
          i++;
        }
        i = 0;
        while (input0.length - i !== 0) {
          input0[i].classList.remove("col-6");
          input0[i].classList.add("col");
          i++;
        }
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.resizeInputFields);
    setTimeout(this.resizeInputFields, 75);
  },
};
</script>

<style scoped>
.option .grip {
  cursor: move;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}
.option .delete {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
